import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import EnhancedHeader from '../components/EnhancedHeader';
import '../styles/CookiePolicy.css';

const CookiePolicy = () => {
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  return (
    <>
      <Helmet>
        <title>Cookie Policy | Active Care Residential</title>
        <meta name="description" content="Cookie Policy for Active Care Residential. Learn about how we use cookies on our website." />
        <meta name="keywords" content="cookie policy, cookies, privacy" />
      </Helmet>

      {/* Enhanced Header */}
      <EnhancedHeader
        title="Cookie Policy"
        subtitle="How we use cookies on our website"
        backgroundImage="/images/residential-care-banner.jpg"
        backgroundGradient="linear-gradient(135deg, rgba(155, 89, 182, 0.7) 0%, rgba(108, 52, 131, 0.7) 100%)"
      />

      <motion.section
        className="cookie-page-wrapper"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <div className="cookie-page-section">
          <div className="cookie-page-container">
            <div className="cookie-page-body">
              <section className="cookie-page-item">
                <h2>What are Cookies?</h2>
                <p>
                  Cookies are small text files that are stored on your device (computer, tablet, or mobile phone) when you visit a website. They contain information about your browsing activity and are used to remember your preferences and enhance your user experience. Cookies are widely used on websites and are essential for their functionality.
                </p>
              </section>

              <section className="cookie-page-item">
                <h2>Why Do We Use Cookies?</h2>
                <p>
                  Active Care Residential uses cookies to enhance your browsing experience and provide improved functionality. We use cookies for:
                </p>
                <ul className="cookie-page-list">
                  <li><strong>Essential Cookies:</strong> Required for website functionality and security</li>
                  <li><strong>Performance Cookies:</strong> Help us understand how visitors use our website</li>
                  <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
                  <li><strong>Marketing Cookies:</strong> Track your activity to provide personalized content (with consent)</li>
                </ul>
              </section>

              <section className="cookie-page-item">
                <h2>Types of Cookies We Use</h2>

                <h3>1. Session Cookies</h3>
                <p>
                  These cookies expire when you close your browser. They are used to maintain your session and provide a seamless browsing experience.
                </p>

                <h3>2. Persistent Cookies</h3>
                <p>
                  These cookies remain on your device for a specified period or until you manually delete them. They help us recognize you when you return to our website.
                </p>

                <h3>3. First-Party Cookies</h3>
                <p>
                  These are set by Active Care Residential directly and contain information about your interactions with our website.
                </p>

                <h3>4. Third-Party Cookies</h3>
                <p>
                  These are set by third-party service providers (e.g., analytics providers, advertising partners) to provide functionality and track usage patterns.
                </p>
              </section>

              <section className="cookie-page-item">
                <h2>Cookie Table</h2>
                <table className="cookie-page-table">
                  <thead>
                    <tr>
                      <th>Cookie Name</th>
                      <th>Purpose</th>
                      <th>Duration</th>
                      <th>Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>_ga</td>
                      <td>Google Analytics - Tracks user interactions</td>
                      <td>2 years</td>
                      <td>Analytics</td>
                    </tr>
                    <tr>
                      <td>_gid</td>
                      <td>Google Analytics - Identifies unique users</td>
                      <td>24 hours</td>
                      <td>Analytics</td>
                    </tr>
                    <tr>
                      <td>PHPSESSID</td>
                      <td>Maintains user session</td>
                      <td>Session</td>
                      <td>Essential</td>
                    </tr>
                    <tr>
                      <td>cookieConsent</td>
                      <td>Remembers your cookie preferences</td>
                      <td>1 year</td>
                      <td>Preference</td>
                    </tr>
                  </tbody>
                </table>
              </section>

              <section className="cookie-page-item">
                <h2>Your Cookie Choices</h2>
                <p>
                  You have control over which cookies are placed on your device:
                </p>
                <ul className="cookie-page-list">
                  <li><strong>Browser Settings:</strong> You can adjust your browser settings to reject or accept cookies</li>
                  <li><strong>Selective Acceptance:</strong> You can accept essential cookies while declining optional ones</li>
                  <li><strong>Opt-Out Options:</strong> You can opt out of analytics and marketing cookies</li>
                  <li><strong>Delete Cookies:</strong> You can delete cookies from your device at any time</li>
                </ul>
              </section>

              <section className="cookie-page-item">
                <h2>How to Manage Cookies in Your Browser</h2>
                <p>Most browsers allow you to control cookies through their settings. Here's how to manage cookies in popular browsers:</p>

                <h3>Chrome</h3>
                <p>
                  Settings → Privacy and security → Cookies and other site data → Manage cookies and site data
                </p>

                <h3>Firefox</h3>
                <p>
                  Preferences → Privacy & Security → Cookies and Site Data
                </p>

                <h3>Safari</h3>
                <p>
                  Preferences → Privacy → Cookies and website data
                </p>

                <h3>Microsoft Edge</h3>
                <p>
                  Settings → Privacy and security → Cookies and other site data
                </p>
              </section>

              <section className="cookie-page-item">
                <h2>Third-Party Cookies</h2>
                <p>
                  Our website may include content from third-party services such as Google Analytics and social media platforms. These third parties may use cookies to track your activity. We recommend reviewing their privacy policies to understand their cookie practices.
                </p>
              </section>

              <section className="cookie-page-item">
                <h2>Do Not Track (DNT)</h2>
                <p>
                  Some browsers include a "Do Not Track" feature. While we respect your privacy preferences, our website currently does not respond to DNT signals. Please use your browser settings or our cookie preferences to control cookie usage.
                </p>
              </section>

              <section className="cookie-page-item">
                <h2>GDPR and Privacy</h2>
                <p>
                  We are committed to GDPR compliance. We obtain explicit consent before placing non-essential cookies on your device. You can withdraw consent at any time by adjusting your cookie preferences. For more information about how we handle your data, please see our <a href="/privacy-policy">Privacy Policy</a>.
                </p>
              </section>

              <section className="cookie-page-item">
                <div className="cookie-page-consent-box">
                  <h3>Managing Your Preferences</h3>
                  <p>
                    You can update your cookie preferences at any time by clicking the "Cookie Settings" button at the bottom of our website. This allows you to accept or reject specific categories of cookies based on your preferences.
                  </p>
                </div>
              </section>

              <section className="cookie-page-item">
                <h2>Updates to This Policy</h2>
                <p>
                  We may update this Cookie Policy periodically to reflect changes in our cookie practices or regulatory requirements. Any significant changes will be communicated through our website. Your continued use of our website signifies your acceptance of the updated Cookie Policy.
                </p>
              </section>

              <section className="cookie-page-item">
                <h2>Contact Us</h2>
                <p>
                  If you have questions about this Cookie Policy or our cookie practices, please contact us:
                </p>
                <div className="cookie-page-contact-box">
                  <p><strong>Active Care Residential</strong></p>
                  <p>Registration Number: 14464980</p>
                  <p>Email: info@activecareresidential.co.uk</p>
                  <p>Phone: +44 7737 207521</p>
                  <p>Address: East Ham, London</p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </motion.section>
    </>
  );
};

export default CookiePolicy;